import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './SubmittalForm.css';
import { Col, Form, FormGroup } from 'react-bootstrap';
import lockPng from 'images/lock.png';
import Dropdown from 'components/shared/dropdown/Dropdown';
import TextareaAutosize from 'react-autosize-textarea';
import Input from 'components/shared/input/Input';
import moment from 'moment';
import { usersUtil, ROLES } from 'services/utils/users-util';
import { Enterprise } from 'domain/enterprise';
import { Project } from 'domain/project';
import {
  SubmittalStatusKeys,
  SubmittalStatusEnum,
  submittalStatusLabel,
} from 'domain/submittal-status-enum';
import { loadProjectProposalRequest } from 'components/admin/projects/details/project-proposal/store/actions';
import { loadDirectoryRequest } from 'components/admin/projects/details/project-directory/store/actions';
import { getProjectProposal } from 'components/admin/projects/details/project-proposal/store/selectors';
import { getDirectory } from 'components/admin/projects/details/project-directory/store/selectors';
import {
  getSubmittalForm,
  getSubmittalReadOnly,
} from 'components/admin/projects/details/project-submittal-log/store/selectors';
import {
  updateSubmittalFormValue,
  clearSubmittalNotes,
} from 'components/admin/projects/details/project-submittal-log/store/actions';

class SubmittalForm extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({}).isRequired,
    project: PropTypes.shape({}).isRequired,
  };

  componentDidMount() {
    this.loadDirectory();
    if (!Enterprise.subcontractor(this.props.project.contributor_type)) {
      this.loadProjectProposal();
    }
  }

  componentWillUnmount() {
    this.props.dispatch(clearSubmittalNotes());
  }

  loadDirectory = () => {
    const { project, directory, dispatch } = this.props;
    if (!directory || directory.project_id !== project.id) {
      dispatch(loadDirectoryRequest(project.id));
    }
  };

  loadProjectProposal = () => {
    const { project, projectProposal, dispatch } = this.props;
    if (!projectProposal || projectProposal.project_id !== project.id) {
      dispatch(loadProjectProposalRequest(project.id));
    }
  };

  handleFormChange = (attribute, value) => {
    this.props.dispatch(updateSubmittalFormValue(attribute, value));
  };

  handleCsiCodeChange = (csiCodeId) => {
    this.handleFormChange('csiCodeId', csiCodeId);
    this.handleFormChange('contractorId', '');

    const subOptions = this.submittalSubcontractorOptions(csiCodeId);
    if (subOptions.length === 1) {
      this.handleFormChange('contractorId', subOptions[0].value);
    }
  };

  submittalCsiCodeOptions = () => {
    return Project.csiCodesOptions(this.props);
  };

  submittalSubcontractorOptions = (csiCodeId) => {
    return Project.subcontractorOptions(this.props, csiCodeId);
  };

  submittalStatusOptions = () => {
    return Object.keys(SubmittalStatusKeys)
      .filter(
        (status) =>
          !SubmittalStatusEnum.DRAFT.equals(SubmittalStatusEnum[status])
      )
      .map((status) => {
        const statusEnum = SubmittalStatusEnum[status];
        return {
          value: statusEnum.status,
          label: `${statusEnum.label
            .charAt(0)
            .toUpperCase()}${statusEnum.label.slice(1)}`,
        };
      });
  };

  render() {
    const { currentUser, project, form } = this.props;
    const subOptions = this.submittalSubcontractorOptions(form.csiCodeId);
    const readOnly =
      this.props.readOnly || Enterprise.primary(project.contributor_type);
    const subcontractor =
      Enterprise.subcontractor(project.contributor_type) ||
      usersUtil.hasRole(currentUser, ROLES.GENERAL_CONTRACTOR_SUPERINTENDENT);
    const submittedBy = form.submittedBy || currentUser;
    const labelStyle = readOnly ? 'compact-control-label' : 'control-label';
    const inputValueStyle = readOnly ? 'read-only' : 'edit';
    const stampAction =
      form.assignee_stamp &&
      form.assignee_stamp.stamp_actions.find(
        (stampAction) => stampAction.action === form.stampAction
      );
    const stampTitle = stampAction && stampAction.title;
    return (
      <div className="submittal-form">
        {<h2 className="submittal-title">Submittal Details</h2>}
        <Form horizontal>
          <FormGroup>
            <Col className={labelStyle} sm={1}>
              Submitted By
            </Col>
            <Col sm={5} className={inputValueStyle}>
              <span className="submitted-by">
                {submittedBy.first_name} {submittedBy.last_name}
              </span>
              {` - ${
                submittedBy.accountable
                  ? submittedBy.accountable.name
                  : 'Spackle'
              }`}
              {currentUser.title ? ` - ${currentUser.title}` : ''}
            </Col>
            <Col className={labelStyle} sm={1}>
              Date Added
            </Col>
            <Col sm={3} className={inputValueStyle}>
              {moment(
                form.submitted_at || form.received_at || undefined
              ).format('M/D/YY')}
            </Col>
          </FormGroup>
          <FormGroup>
            <Col className={labelStyle} sm={1}>
              Title
            </Col>
            <Col sm={5} className={inputValueStyle}>
              {readOnly ? (
                form.title ? (
                  form.title
                ) : (
                  '-'
                )
              ) : (
                <TextareaAutosize
                  className={classnames({ 'form-error': form.errors.title })}
                  rows={1}
                  placeholder="Enter Text Here..."
                  defaultValue={form.title}
                  onBlur={(e) => this.handleFormChange('title', e.target.value)}
                />
              )}
            </Col>
            <Col className={labelStyle} sm={1}>
              Status
            </Col>
            <Col sm={3} className={inputValueStyle}>
              {subcontractor ? (
                <span className="status-locked">
                  {submittalStatusLabel({ ...form, stamp_title: stampTitle })}
                  <img className="locked-icon" src={lockPng} alt="locked" />
                </span>
              ) : (
                <Dropdown
                  className={classnames({ 'form-error': form.errors.status })}
                  placeholder="Select Status..."
                  value={form.status}
                  onChange={(option) =>
                    this.handleFormChange('status', option.value)
                  }
                  options={this.submittalStatusOptions()}
                />
              )}
            </Col>
          </FormGroup>
          <FormGroup>
            <Col className={labelStyle} sm={1}>
              CSI Code
            </Col>
            <Col sm={5} className={readOnly || form.id ? inputValueStyle : ''}>
              {readOnly ? (
                form.csiCodeLabel
              ) : (
                <Dropdown
                  className={classnames({
                    'dropdown-error': form.errors.csiCodeId,
                  })}
                  placeholder="Select Code..."
                  value={form.csiCodeId}
                  onChange={(option) => this.handleCsiCodeChange(option.value)}
                  options={this.submittalCsiCodeOptions()}
                />
              )}
            </Col>
            <Col className={labelStyle} sm={1}>
              Physical Copies
            </Col>
            <Col sm={3} className={inputValueStyle}>
              {readOnly ? (
                form.physicalCopies
              ) : (
                <Input
                  type="number"
                  value={form.physicalCopies}
                  onChange={(value) =>
                    this.handleFormChange('physicalCopies', value)
                  }
                />
              )}
            </Col>
          </FormGroup>
          {(readOnly || (!subcontractor && subOptions.length > 1)) && (
            <FormGroup>
              <Col className={labelStyle} sm={1}>
                Subcontractor
              </Col>
              <Col sm={5} className={inputValueStyle}>
                {readOnly ? (
                  form.contractor ? (
                    form.contractor.name
                  ) : (
                    '-'
                  )
                ) : (
                  <Dropdown
                    placeholder="Select Subcontractor..."
                    value={form.contractorId}
                    onChange={(option) =>
                      this.handleFormChange('contractorId', option.value)
                    }
                    options={subOptions}
                  />
                )}
              </Col>
            </FormGroup>
          )}
        </Form>
      </div>
    );
  }
}

export default connect((state) => {
  return {
    projectProposal: getProjectProposal(state),
    directory: getDirectory(state),
    form: getSubmittalForm(state),
    readOnly: getSubmittalReadOnly(state),
  };
})(SubmittalForm);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-submittal-log/SubmittalForm.js